import React, { useEffect, useRef, useState } from 'react';
import { openA11yDialog } from './plugins/a11y';
import { openFormulaDialog } from './plugins/formula';
import { openWidgetByAction, INTERACTIVE_MENU_ITEMS } from './widgets';

const clickToolbarButton = (editor, name) => {
  const button = editor?.container?.querySelector(`[data-ref="${name}"]`);
  if (!button) {
    if (editor?.execCommand) {
      editor.execCommand(name);
    }
    return;
  }
  const trigger =
    button.querySelector('.jodit-toolbar-button__button') ||
    button.querySelector('button') ||
    button;
  trigger.click();
};

const execControl = (editor, name) => {
  const control = editor?.o?.controls?.[name];
  if (typeof control?.exec === 'function') {
    control.exec(editor, editor.s?.current?.(), { control });
    return true;
  }
  return false;
};

const applyBlock = (editor, tag) => {
  editor.s?.focus?.();
  if (editor.s?.commitStyle) {
    editor.s.commitStyle({ element: tag });
    editor.synchronizeValues?.();
    return;
  }
  editor.execCommand('formatBlock', false, tag);
};

const applyInlineStyle = (editor, style) => {
  editor.s?.focus?.();
  if (editor.s?.commitStyle) {
    editor.s.commitStyle({
      element: 'span',
      attributes: { style },
    });
    editor.synchronizeValues?.();
    return;
  }
  const [[prop, value]] = Object.entries(style);
  if (prop === 'fontFamily') {
    editor.execCommand('fontName', false, value);
    return;
  }
  if (prop === 'fontSize') {
    editor.execCommand('fontSize', false, value);
    return;
  }
  editor.execCommand('style', false, `${prop}: ${value}`);
};

const runAction = (editor, action, value) => {
  if (!editor) {
    return;
  }

  switch (action) {
    case 'newdocument':
      editor.confirm('Clear all content from this document?', (yes) => {
        if (yes) {
          editor.value = '';
        }
      });
      return;
    case 'print':
      if (!execControl(editor, 'print')) {
        clickToolbarButton(editor, 'print');
      }
      return;
    case 'undo':
    case 'redo':
    case 'cut':
    case 'copy':
    case 'paste':
    case 'selectall':
    case 'bold':
    case 'italic':
    case 'underline':
    case 'strikethrough':
    case 'superscript':
    case 'subscript':
      editor.execCommand(action);
      return;
    case 'code':
      applyBlock(editor, 'code');
      return;
    case 'eraser':
      editor.execCommand('removeFormat');
      return;
    case 'find':
      editor.execCommand('openSearchDialog');
      return;
    case 'preview':
      if (!execControl(editor, 'preview')) {
        editor.execCommand('preview');
      }
      return;
    case 'source':
      clickToolbarButton(editor, 'source');
      return;
    case 'fullsize':
      editor.toggleFullSize();
      return;
    case 'hr':
      editor.execCommand('insertHorizontalRule');
      return;
    case 'formula':
      openFormulaDialog(editor);
      return;
    case 'a11yCheck':
      openA11yDialog(editor);
      return;
    case 'formatBlock':
      applyBlock(editor, value || 'p');
      return;
    case 'fontName':
      applyInlineStyle(editor, { fontFamily: value });
      return;
    case 'fontSize':
      applyInlineStyle(editor, { fontSize: value });
      return;
    case 'lineHeight':
      applyInlineStyle(editor, { lineHeight: value });
      return;
    case 'left':
    case 'center':
    case 'right':
    case 'justify':
      clickToolbarButton(editor, action);
      return;
    case 'foreColor':
    case 'backColor':
    case 'brush':
      clickToolbarButton(editor, 'brush');
      return;
    case 'image':
    case 'video':
    case 'file':
    case 'link':
    case 'table':
    case 'symbols':
    case 'font':
    case 'fontsize':
      clickToolbarButton(editor, action);
      return;
    default:
      if (openWidgetByAction(editor, action)) {
        return;
      }
      clickToolbarButton(editor, action);
  }
};

const HEADING_ITEMS = [
  { action: 'formatBlock', value: 'h1', label: 'Heading 1', preview: 'h1' },
  { action: 'formatBlock', value: 'h2', label: 'Heading 2', preview: 'h2' },
  { action: 'formatBlock', value: 'h3', label: 'Heading 3', preview: 'h3' },
  { action: 'formatBlock', value: 'h4', label: 'Heading 4', preview: 'h4' },
  { action: 'formatBlock', value: 'h5', label: 'Heading 5', preview: 'h5' },
  { action: 'formatBlock', value: 'h6', label: 'Heading 6', preview: 'h6' },
];

const ALIGN_ITEMS = [
  { action: 'left', label: 'Left' },
  { action: 'center', label: 'Center' },
  { action: 'right', label: 'Right' },
  { action: 'justify', label: 'Justify' },
];

const FONT_ITEMS = [
  'Arial',
  'Georgia',
  'Times New Roman',
  'Courier New',
  'Verdana',
  'Tahoma',
  'Trebuchet MS',
].map((font) => ({ action: 'fontName', value: font, label: font }));

const FONT_SIZE_ITEMS = [
  { action: 'fontSize', value: '10px', label: '8pt' },
  { action: 'fontSize', value: '13px', label: '10pt' },
  { action: 'fontSize', value: '16px', label: '12pt' },
  { action: 'fontSize', value: '19px', label: '14pt' },
  { action: 'fontSize', value: '24px', label: '18pt' },
  { action: 'fontSize', value: '32px', label: '24pt' },
  { action: 'fontSize', value: '48px', label: '36pt' },
];

const LINE_HEIGHT_ITEMS = [
  { action: 'lineHeight', value: '1', label: '1' },
  { action: 'lineHeight', value: '1.15', label: '1.15' },
  { action: 'lineHeight', value: '1.5', label: '1.5' },
  { action: 'lineHeight', value: '2', label: '2' },
];

const MENUS = [
  {
    id: 'file',
    label: 'File',
    items: [
      { action: 'newdocument', label: 'New document' },
      { action: 'print', label: 'Print...', shortcut: 'Ctrl+P' },
    ],
  },
  {
    id: 'edit',
    label: 'Edit',
    items: [
      { action: 'undo', label: 'Undo', shortcut: 'Ctrl+Z' },
      { action: 'redo', label: 'Redo', shortcut: 'Ctrl+Y' },
      { type: 'separator' },
      { action: 'cut', label: 'Cut', shortcut: 'Ctrl+X' },
      { action: 'copy', label: 'Copy', shortcut: 'Ctrl+C' },
      { action: 'paste', label: 'Paste', shortcut: 'Ctrl+V' },
      { action: 'selectall', label: 'Select all', shortcut: 'Ctrl+A' },
      { type: 'separator' },
      { action: 'find', label: 'Find and replace...', shortcut: 'Ctrl+F' },
    ],
  },
  {
    id: 'view',
    label: 'View',
    items: [
      { action: 'preview', label: 'Preview' },
      { action: 'source', label: 'Source code' },
      { action: 'fullsize', label: 'Fullscreen' },
    ],
  },
  {
    id: 'insert',
    label: 'Insert',
    items: [
      { action: 'image', label: 'Image...' },
      { action: 'video', label: 'Media...' },
      { action: 'file', label: 'File...' },
      { type: 'separator' },
      { action: 'link', label: 'Link...', shortcut: 'Ctrl+K' },
      { action: 'table', label: 'Table' },
      { action: 'hr', label: 'Horizontal line' },
      { action: 'symbols', label: 'Special character...' },
      { type: 'separator' },
      { action: 'formula', label: 'Mathematical formula...' },
      { action: 'aiButton', label: 'Ai Review' },
    ],
    interactiveItems: INTERACTIVE_MENU_ITEMS,
  },
  {
    id: 'format',
    label: 'Format',
    items: [
      { action: 'bold', label: 'Bold', shortcut: 'Ctrl+B' },
      { action: 'italic', label: 'Italic', shortcut: 'Ctrl+I' },
      { action: 'underline', label: 'Underline', shortcut: 'Ctrl+U' },
      { action: 'strikethrough', label: 'Strikethrough' },
      { action: 'superscript', label: 'Superscript' },
      { action: 'subscript', label: 'Subscript' },
      { action: 'code', label: 'Code' },
      { type: 'separator' },
      {
        type: 'submenu',
        label: 'Formats',
        items: [
          { type: 'submenu', label: 'Headings', items: HEADING_ITEMS },
          {
            type: 'submenu',
            label: 'Inline',
            items: [
              { action: 'bold', label: 'Bold' },
              { action: 'italic', label: 'Italic' },
              { action: 'underline', label: 'Underline' },
              { action: 'strikethrough', label: 'Strikethrough' },
              { action: 'superscript', label: 'Superscript' },
              { action: 'subscript', label: 'Subscript' },
              { action: 'code', label: 'Code' },
            ],
          },
          {
            type: 'submenu',
            label: 'Blocks',
            items: [
              { action: 'formatBlock', value: 'p', label: 'Paragraph' },
              { action: 'formatBlock', value: 'blockquote', label: 'Blockquote' },
              { action: 'formatBlock', value: 'pre', label: 'Pre' },
              { action: 'formatBlock', value: 'div', label: 'Div' },
            ],
          },
          { type: 'submenu', label: 'Align', items: ALIGN_ITEMS },
        ],
      },
      {
        type: 'submenu',
        label: 'Blocks',
        items: [
          { action: 'formatBlock', value: 'p', label: 'Paragraph' },
          ...HEADING_ITEMS,
          { action: 'formatBlock', value: 'blockquote', label: 'Blockquote' },
        ],
      },
      { type: 'submenu', label: 'Fonts', items: FONT_ITEMS },
      { type: 'submenu', label: 'Font sizes', items: FONT_SIZE_ITEMS },
      { type: 'submenu', label: 'Align', items: ALIGN_ITEMS },
      { type: 'submenu', label: 'Line height', items: LINE_HEIGHT_ITEMS },
      { type: 'separator' },
      { action: 'foreColor', label: 'Text colour' },
      { action: 'backColor', label: 'Background colour' },
      { action: 'eraser', label: 'Clear formatting' },
    ],
  },
  {
    id: 'tools',
    label: 'Tools',
    items: [
      { action: 'a11yCheck', label: 'Accessibility checker' },
      { action: 'find', label: 'Find and replace...', shortcut: 'Ctrl+F' },
      { action: 'source', label: 'Source code' },
      { action: 'preview', label: 'Preview' },
    ],
  },
  {
    id: 'table',
    label: 'Table',
    items: [{ action: 'table', label: 'Insert table' }],
  },
];

const getMenuItems = (menu, enableInteractiveWidgets) => {
  if (!enableInteractiveWidgets || !menu.interactiveItems?.length) {
    return menu.items;
  }
  return [
    ...menu.items,
    { type: 'separator' },
    { type: 'header', label: 'Interactive' },
    ...menu.interactiveItems,
  ];
};

const itemKey = (item, index) => {
  if (item.type === 'separator') {
    return `sep-${index}`;
  }
  if (item.type === 'header') {
    return `header-${item.label}-${index}`;
  }
  if (item.type === 'submenu') {
    return `sub-${item.label}-${index}`;
  }
  return `${item.action}-${item.value || ''}-${index}`;
};

const MenuItems = ({ items, prefix, onAction }) =>
  items.map((item, index) => {
    const key = `${prefix}-${itemKey(item, index)}`;
    if (item.type === 'separator') {
      return <div key={key} className="jodit-menubar__separator" />;
    }
    if (item.type === 'header') {
      return (
        <div key={key} className="jodit-menubar__header">
          {item.label}
        </div>
      );
    }
    if (item.type === 'submenu') {
      return (
        <div key={key} className="jodit-menubar__submenu">
          <button
            type="button"
            className="jodit-menubar__option jodit-menubar__option--submenu"
            role="menuitem"
            aria-haspopup="true"
          >
            <span>{item.label}</span>
            <span className="jodit-menubar__chevron" aria-hidden="true">
              ›
            </span>
          </button>
          <div className="jodit-menubar__dropdown jodit-menubar__dropdown--nested">
            <MenuItems
              items={item.items}
              prefix={key}
              onAction={onAction}
            />
          </div>
        </div>
      );
    }
    return (
      <button
        key={key}
        type="button"
        className={`jodit-menubar__option${
          item.preview ? ` jodit-menubar__option--${item.preview}` : ''
        }`}
        role="menuitem"
        onClick={() => onAction(item.action, item.value)}
      >
        <span>{item.label}</span>
        {item.shortcut ? (
          <span className="jodit-menubar__shortcut">{item.shortcut}</span>
        ) : null}
      </button>
    );
  });

const JoditMenuBar = ({
  editorRef,
  disabled,
  onAiOpen,
  enableInteractiveWidgets = false,
}) => {
  const [openMenu, setOpenMenu] = useState(null);
  const barRef = useRef(null);

  useEffect(() => {
    const onPointerDown = (event) => {
      if (!barRef.current?.contains(event.target)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  const handleAction = (action, value) => {
    const editor = editorRef.current?.editor || editorRef.current;
    setOpenMenu(null);
    if (action === 'aiButton') {
      onAiOpen(editor);
      return;
    }
    runAction(editor, action, value);
  };

  return (
    <div className="jodit-menubar" ref={barRef}>
      {MENUS.map((menu) => (
        <div
          key={menu.id}
          className={`jodit-menubar__item${
            openMenu === menu.id ? ' jodit-menubar__item--open' : ''
          }`}
        >
          <button
            type="button"
            className="jodit-menubar__button"
            disabled={disabled}
            aria-haspopup="true"
            aria-expanded={openMenu === menu.id}
            onClick={() =>
              setOpenMenu((current) => (current === menu.id ? null : menu.id))
            }
          >
            {menu.label}
          </button>
          {openMenu === menu.id && (
            <div className="jodit-menubar__dropdown" role="menu">
              <MenuItems
                items={getMenuItems(menu, enableInteractiveWidgets)}
                prefix={menu.id}
                onAction={handleAction}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default JoditMenuBar;
